import FolderClient from '../client/folder/FolderClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import React from 'react';
import { CaseSettings, CaseActionStatus } from '../domain/case/CaseDomains';
import CaseSettingsClient from '../client/integration/configuration/CaseSettingsClient';
import { query } from '@trlogic/client-js';
import Query = query.Query;
import { dispatch } from '../redux/store';
import { changeApiVariableDirty } from '../redux/actions/ApiVariableActions';
import DeployErrorUtil from '../util/DeployErrorUtil';

class DomainConfigurationService {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private domainConfigurationClient: CaseSettingsClient = GatewayContainer.getInstance().get(GatewayNames.DOMAIN_CONFIGURATION_CLIENT);

  public saveDomainConfiguration = async (domain: CaseSettings, isUpdate: boolean, folder: EnrichedComponentCategory) => {
    try {
      if (domain == undefined || domain.name == undefined) return false;
      if (domain.actions) {
        domain.actions = domain.actions.map((a) => ({
          ...a,
          status: a.status || CaseActionStatus.ALL,
        }));
      }
      await this.domainConfigurationClient.save(domain).catch((e) => console.error(e));
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.CONFIGURATION, folder.id, domain.name);
      }
      MessageUtil.success({
        summary: 'Configuration',
        detail: (
          <div>
            <b>{domain.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved.' })}
          </div>
        ),
      });
      dispatch(changeApiVariableDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: 'Configuration',
        detail: (
          <div>
            <b>{domain.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  };

  public removeDomainConfiguration = async (name: string): Promise<boolean> => {
    try {
      if (name) {
        await this.domainConfigurationClient.remove(name);
        MessageUtil.success({
          summary: 'Configuration',
          detail: (
            <div>
              <b>{name}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}
            </div>
          ),
        });
        return true;
      }
      return false;
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.config', defaultMessage: 'Configuration' }))) {
        return false;
      }
      MessageUtil.error({
        summary: 'Configuration',
        detail: (
          <div>
            <b>{name}</b>
            {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  };

  public async removeAll(domains: CaseSettings[]) {
    try {
      for (const domain of domains) {
        await this.removeDomainConfiguration(domain.name);
      }
    } catch (e) {
      MessageUtil.error({
        content: 'Error while removing domains',
      });
    }
  }

  public getByChannel(id: string) {
    return this.domainConfigurationClient.getByChannel(id);
  }

  public async count(_query: Query): Promise<number> {
    return this.domainConfigurationClient.count(_query);
  }
}

export default DomainConfigurationService;
